import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CHAIN_ID = 'amitis-network';
export const REST = 'https://rest.amitis.network';
export const RPC  = 'https://rpc.amitis.network';

const CHAIN_CONFIG = {
  chainId: CHAIN_ID,
  chainName: 'Amitis Network',
  rpc: RPC, rest: REST,
  bip44: { coinType: 118 },
  bech32Config: {
    bech32PrefixAccAddr: 'amitis', bech32PrefixAccPub: 'amitispub',
    bech32PrefixValAddr: 'amitisvaloper', bech32PrefixValPub: 'amitisvaloperpub',
    bech32PrefixConsAddr: 'amitisvalcons', bech32PrefixConsPub: 'amitisvalconspub',
  },
  currencies: [{ coinDenom: 'AMTS', coinMinimalDenom: 'uamts', coinDecimals: 6 }],
  feeCurrencies: [{ coinDenom: 'AMTS', coinMinimalDenom: 'uamts', coinDecimals: 6,
    gasPriceStep: { low: 0.01, average: 0.025, high: 0.04 } }],
  stakeCurrency: { coinDenom: 'AMTS', coinMinimalDenom: 'uamts', coinDecimals: 6 },
  features: ['ibc-transfer', 'cosmwasm'],
};

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  // Keplr state
  const [keplrAddress, setKeplrAddress] = useState(null);
  const [keplrBalance, setKeplrBalance] = useState(0);
  // Native wallet state (set externally by useNativeWallet via context)
  const [nativeAddress, setNativeAddress] = useState(null);
  const [nativeBalance, setNativeBalance] = useState(0);
  const [nativeWalletRef, setNativeWalletRef] = useState(null);
  // Shared
  const [blockHeight, setBlockHeight] = useState(null);

  // Unified — native takes priority
  const address = nativeAddress || keplrAddress;
  const balance = nativeAddress ? nativeBalance : keplrBalance;

  const fetchBalance = useCallback(async (addr) => {
    try {
      const res  = await fetch(`${REST}/cosmos/bank/v1beta1/balances/${addr}`);
      const data = await res.json();
      const u    = (data.balances || []).find(b => b.denom === 'uamts');
      return u ? parseInt(u.amount) / 1_000_000 : 0;
    } catch { return 0; }
  }, []);

  // Keplr connect
  const connectKeplr = useCallback(async () => {
    if (!window.keplr) throw new Error('Keplr not detected. Please install the Keplr extension.');
    await window.keplr.experimentalSuggestChain(CHAIN_CONFIG);
    await window.keplr.enable(CHAIN_ID);
    const signer   = window.keplr.getOfflineSigner(CHAIN_ID);
    const accounts = await signer.getAccounts();
    if (!accounts.length) throw new Error('No accounts found.');
    const addr = accounts[0].address;
    setKeplrAddress(addr);
    const bal = await fetchBalance(addr);
    setKeplrBalance(bal);
    return addr;
  }, [fetchBalance]);

  // Register native wallet (called from useNativeWallet)
  const registerNative = useCallback((addr, bal, walletRef) => {
    setNativeAddress(addr);
    setNativeBalance(bal);
    setNativeWalletRef(walletRef);
  }, []);

  const clearNative = useCallback(() => {
    setNativeAddress(null);
    setNativeBalance(0);
    setNativeWalletRef(null);
  }, []);

  const disconnectKeplr = useCallback(() => {
    setKeplrAddress(null);
    setKeplrBalance(0);
  }, []);

  const disconnect = useCallback(() => {
    if (nativeAddress) clearNative();
    if (keplrAddress) disconnectKeplr();
  }, [nativeAddress, keplrAddress, clearNative, disconnectKeplr]);

  const refresh = useCallback(async () => {
    if (!address) return;
    const bal = await fetchBalance(address);
    if (nativeAddress) setNativeBalance(bal);
    else setKeplrBalance(bal);
  }, [address, nativeAddress, fetchBalance]);

  // Auto-reconnect Keplr
  useEffect(() => {
    if (!window.keplr) return;
    (async () => {
      try {
        await window.keplr.enable(CHAIN_ID);
        const signer   = window.keplr.getOfflineSigner(CHAIN_ID);
        const accounts = await signer.getAccounts();
        if (accounts?.length) {
          setKeplrAddress(accounts[0].address);
          setKeplrBalance(await fetchBalance(accounts[0].address));
        }
      } catch {}
    })();
  }, [fetchBalance]);

  // Block height polling
  useEffect(() => {
    const poll = async () => {
      try {
        const res  = await fetch(`${RPC}/status`);
        const data = await res.json();
        const h    = data.result?.sync_info?.latest_block_height;
        if (h) setBlockHeight(parseInt(h).toLocaleString());
      } catch {}
    };
    poll();
    const id = setInterval(poll, 6000);
    return () => clearInterval(id);
  }, []);

  // Keplr keystore change
  useEffect(() => {
    const handler = async () => {
      if (!window.keplr || !keplrAddress) return;
      try {
        const signer = window.keplr.getOfflineSigner(CHAIN_ID);
        const accts  = await signer.getAccounts();
        if (accts?.length) {
          setKeplrAddress(accts[0].address);
          setKeplrBalance(await fetchBalance(accts[0].address));
        }
      } catch {}
    };
    window.addEventListener('keplr_keystorechange', handler);
    return () => window.removeEventListener('keplr_keystorechange', handler);
  }, [keplrAddress, fetchBalance]);

  const shortAddr = address ? address.slice(0, 12) + '…' + address.slice(-4) : null;
  const walletType = nativeAddress ? 'native' : keplrAddress ? 'keplr' : null;

  return (
    <WalletContext.Provider value={{
      // Unified
      address, balance, walletType, shortAddr,
      disconnect, refresh, fetchBalance,
      // Keplr specific
      connectKeplr, keplrAddress, keplrBalance,
      // Native registration
      registerNative, clearNative, nativeWalletRef,
      // Shared
      blockHeight, REST, RPC, CHAIN_ID, CHAIN_CONFIG,
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() { return useContext(WalletContext); }
